import { useState } from "react";
import axios from "axios";
import { LockKeyhole, LogOut, FileDown, Inbox, Loader2, Mail, Check, RotateCcw, Search, Trash2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { LigeriaWordmark } from "@/components/Logo";
import { API } from "@/lib/api";

const CSV_HEADERS = ["Date", "Société", "Marque", "SIRET", "Référence", "Sélection", "Quantité", "Prénom", "Nom", "E-mail", "Téléphone", "Message", "Statut"];
const PER_PAGE = 20;

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const dayKey = (iso) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const buildByDay = (rows) => {
  const map = new Map();
  for (const r of rows) {
    const key = dayKey(r.created_at);
    if (!map.has(key)) map.set(key, { key, ts: new Date(r.created_at).setHours(0, 0, 0, 0), total: 0, tremblay: 0, panther: 0 });
    const e = map.get(key);
    e.total += 1;
    if (r.brand === "PANTHER") e.panther += 1;
    else e.tremblay += 1;
  }
  return [...map.values()].sort((a, b) => b.ts - a.ts).slice(0, 14);
};

const weekStart = (iso) => {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};

const buildByWeek = (rows) => {
  const map = new Map();
  for (const r of rows) {
    const ws = weekStart(r.created_at);
    const key = ws.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
    if (!map.has(key)) map.set(key, { key, ts: ws.getTime(), total: 0, tremblay: 0, panther: 0 });
    const e = map.get(key);
    e.total += 1;
    if (r.brand === "PANTHER") e.panther += 1;
    else e.tremblay += 1;
  }
  return [...map.values()].sort((a, b) => b.ts - a.ts).slice(0, 8);
};

const replyHref = (r) => {
  const subject = `Votre demande ${r.reference} — Ligéria Sport Concept`;
  const body = [
    `Bonjour ${(r.first_name || "") + " " + (r.last_name || "")}`.trim() + ",",
    "",
    `Nous avons bien reçu votre demande concernant : ${r.reference} (marque ${r.brand || "TREMBLAY"}${r.quantity ? `, quantité ${r.quantity}` : ""}).`,
    "",
    `Société : ${r.company}`,
    `SIRET : ${r.siret}`,
    "",
    "Vous trouverez ci-dessous notre proposition commerciale.",
    "",
    "Cordialement,",
    "Ligéria Sport Concept",
  ].join("\r\n");
  return `mailto:${r.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const isProcessed = (r) => r.processed === true;

const STATUS_TABS = [
  { key: "all", label: "Toutes" },
  { key: "new", label: "Nouvelles" },
  { key: "done", label: "Traitées" },
];

const COLUMNS = [
  { key: "date", label: "Date", sortable: true },
  { key: "societe", label: "Société", sortable: true },
  { key: "marque", label: "Marque", sortable: true },
  { key: null, label: "SIRET", sortable: false },
  { key: "reference", label: "Référence", sortable: true },
  { key: null, label: "Qté", sortable: false },
  { key: null, label: "Contact", sortable: false },
  { key: null, label: "E-mail", sortable: false },
  { key: "statut", label: "Statut", sortable: true },
  { key: null, label: "Message", sortable: false },
  { key: null, label: "", sortable: false },
];

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem("lsc-admin-token") || "");
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [rowBusy, setRowBusy] = useState("");
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quote-requests`, { headers: { "x-admin-token": token } });
      sessionStorage.setItem("lsc-admin-token", token);
      setRows(data);
      setAuthed(true);
      setPage(1);
      try {
        const { data: c } = await axios.get(`${API}/quote-requests/count`);
        localStorage.setItem("lsc-seen-count", String(c.count));
      } catch {
        /* pastille non critique */
      }
    } catch (err) {
      setError(err?.response?.status === 401 ? "Clé incorrecte." : "Service indisponible, réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("lsc-admin-token");
    setToken("");
    setRows([]);
    setAuthed(false);
  };

  const toggleProcessed = async (r) => {
    setRowBusy(r.id);
    try {
      const { data } = await axios.patch(
        `${API}/quote-requests/${r.id}`,
        { processed: !isProcessed(r) },
        { headers: { "x-admin-token": token } }
      );
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, processed: data.processed } : x)));
    } catch {
      /* silence : l'état reste inchangé */
    } finally {
      setRowBusy("");
    }
  };

  const deleteRow = async (r) => {
    if (!window.confirm("Supprimer définitivement cette demande ? Cette action est irréversible.")) return;
    setRowBusy(r.id);
    try {
      await axios.delete(`${API}/quote-requests/${r.id}`, { headers: { "x-admin-token": token } });
      setRows((prev) => prev.filter((x) => x.id !== r.id));
    } catch {
      /* silence */
    } finally {
      setRowBusy("");
    }
  };

  const exportCsv = () => {
    const lines = [CSV_HEADERS.join(";")];
    for (const r of rows) {
      lines.push([fmtDate(r.created_at), r.company, r.brand || "TREMBLAY", r.siret, r.reference, (r.selection || []).join(" | "), r.quantity, r.first_name, r.last_name, r.email, r.phone, r.message, isProcessed(r) ? "Traité" : "Nouvelle"].map(csvCell).join(";"));
    }
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "demandes-ligeria.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const q = query.trim().toLowerCase();
  const visible = rows.filter((r) => {
    if (statusFilter === "new" && isProcessed(r)) return false;
    if (statusFilter === "done" && !isProcessed(r)) return false;
    if (!q) return true;
    return `${r.reference} ${r.company} ${r.brand || "TREMBLAY"}`.toLowerCase().includes(q);
  });

  const sorted = [...visible].sort((a, b) => {
    let v = 0;
    if (sortKey === "date") v = new Date(a.created_at) - new Date(b.created_at);
    else if (sortKey === "marque") v = (a.brand || "TREMBLAY").localeCompare(b.brand || "TREMBLAY");
    else if (sortKey === "societe") v = (a.company || "").localeCompare(b.company || "");
    else if (sortKey === "reference") v = (a.reference || "").localeCompare(b.reference || "");
    else if (sortKey === "statut") v = (a.processed === true) - (b.processed === true);
    return sortDir === "asc" ? v : -v;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pageRows = sorted.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  const sortBy = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "date" ? "desc" : "asc");
    }
    setPage(1);
  };

  const nbPanther = rows.filter((r) => r.brand === "PANTHER").length;
  const nbTremblay = rows.length - nbPanther;
  const nbNew = rows.filter((r) => !isProcessed(r)).length;
  const byDay = buildByDay(rows);
  const maxDay = Math.max(1, ...byDay.map((d) => d.total));
  const byWeek = buildByWeek(rows);
  const maxWeek = Math.max(1, ...byWeek.map((w) => w.total));

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="admin-page">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-5 md:px-10 h-[72px] flex items-center justify-between">
          <LigeriaWordmark compact />
          {authed && (
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 text-sm text-white/60 hover:text-blaze transition-colors"
            >
              <LogOut className="h-4 w-4" /> Déconnexion
            </button>
          )}
        </div>
      </header>

      {!authed ? (
        <div className="flex items-center justify-center px-5" style={{ minHeight: "calc(100vh - 72px)" }}>
          <form onSubmit={login} className="w-full max-w-sm bg-panel border border-white/10 p-10" data-testid="admin-login-form">
            <LockKeyhole className="h-8 w-8 text-blaze mb-6" />
            <h1 className="font-display uppercase text-3xl tracking-tight">Espace Ligéria</h1>
            <p className="mt-3 text-sm text-white/50">Accès réservé — saisissez votre clé d'administration.</p>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              data-testid="admin-token-input"
              placeholder="Clé d'administration"
              className="mt-6 w-full bg-zinc-900 border border-zinc-800 rounded-none h-12 px-4 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-blaze focus:ring-1 focus:ring-blaze"
            />
            {error && <p data-testid="admin-error" className="mt-3 text-blaze text-xs">{error}</p>}
            <button
              type="submit"
              disabled={loading || !token}
              data-testid="admin-login-btn"
              className="mt-6 w-full py-3.5 bg-blaze text-white font-accent uppercase tracking-[0.25em] text-sm hover:-translate-y-0.5 hover:shadow-[0_12px_35px_rgba(255,46,0,0.35)] transition-all duration-200 disabled:opacity-50 disabled:translate-y-0"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Accéder"}
            </button>
          </form>
        </div>
      ) : (
        <main className="mx-auto max-w-7xl px-5 md:px-10 py-14">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
            <div>
              <p className="text-sm text-blaze">Administration</p>
              <h2 className="font-display uppercase text-4xl tracking-tight mt-3">
                Demandes reçues <span className="text-white/40">({rows.length})</span>
              </h2>
              <p className="text-sm text-white/50 mt-2">{nbNew} nouvelle{nbNew > 1 ? "s" : ""} à traiter</p>
            </div>
            <button
              onClick={exportCsv}
              data-testid="admin-export-csv"
              className="w-fit flex items-center gap-3 px-7 py-3.5 bg-blaze text-white font-accent uppercase tracking-[0.22em] text-sm hover:-translate-y-0.5 hover:shadow-[0_12px_35px_rgba(255,46,0,0.35)] transition-all duration-200"
            >
              <FileDown className="h-4 w-4" /> Exporter en CSV
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="admin-stats">
            <div className="bg-panel border border-white/10 p-5 md:p-6" data-testid="admin-stat-total">
              <span className="font-display text-3xl md:text-4xl text-white">{rows.length}</span>
              <span className="block text-xs text-white/50 mt-3">Total</span>
            </div>
            <div className="bg-panel border border-white/10 p-5 md:p-6" data-testid="admin-stat-new">
              <span className="font-display text-3xl md:text-4xl text-white">{nbNew}</span>
              <span className="block text-xs text-white/50 mt-3">Nouvelles</span>
            </div>
            <div className="bg-panel border border-white/10 p-5 md:p-6" data-testid="admin-stat-tremblay">
              <span className="font-display text-3xl md:text-4xl text-blaze">{nbTremblay}</span>
              <span className="block text-xs text-white/50 mt-3">Tremblay</span>
            </div>
            <div className="bg-panel border border-[#C32A3A]/50 p-5 md:p-6" data-testid="admin-stat-panther">
              <span className="font-display text-3xl md:text-4xl text-[#E8607A]">{nbPanther}</span>
              <span className="block text-xs text-white/50 mt-3">Panther</span>
            </div>
          </div>

          {rows.length > 0 && (
            <div className="bg-panel border border-white/10 p-6 md:p-8 mb-10" data-testid="admin-daily-stats">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <p className="text-sm text-white/40">Répartition par jour</p>
                <p className="flex items-center gap-5 text-xs text-white/40">
                  <span className="flex items-center gap-2"><span className="w-3 h-3 bg-blaze inline-block" /> Tremblay</span>
                  <span className="flex items-center gap-2"><span className="w-3 h-3 bg-[#E8607A] inline-block" /> Panther</span>
                </p>
              </div>
              <div className="space-y-3">
                {byDay.map((d) => (
                  <div key={d.key} className="flex items-center gap-4" data-testid="admin-day-row">
                    <span className="font-mono text-xs text-white/50 w-24 shrink-0">{d.key}</span>
                    <div className="flex-1 h-6 bg-white/5 flex overflow-hidden">
                      {d.tremblay > 0 && (
                        <div className="h-full bg-blaze" style={{ width: `${(d.tremblay / maxDay) * 100}%` }} title={`Tremblay : ${d.tremblay}`} />
                      )}
                      {d.panther > 0 && (
                        <div className="h-full bg-[#E8607A]" style={{ width: `${(d.panther / maxDay) * 100}%` }} title={`Panther : ${d.panther}`} />
                      )}
                    </div>
                    <span className="font-display text-lg text-white w-10 text-right shrink-0">{d.total}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {rows.length > 0 && (
            <div className="bg-panel border border-white/10 p-6 md:p-8 mb-10" data-testid="admin-weekly-stats">
              <p className="text-sm text-white/40 mb-6">Répartition par semaine</p>
              <div className="space-y-3">
                {byWeek.map((w) => (
                  <div key={w.key} className="flex items-center gap-4" data-testid="admin-week-row">
                    <span className="font-mono text-xs text-white/50 w-24 shrink-0">sem. {w.key}</span>
                    <div className="flex-1 h-6 bg-white/5 flex overflow-hidden">
                      {w.tremblay > 0 && (
                        <div className="h-full bg-blaze" style={{ width: `${(w.tremblay / maxWeek) * 100}%` }} title={`Tremblay : ${w.tremblay}`} />
                      )}
                      {w.panther > 0 && (
                        <div className="h-full bg-[#E8607A]" style={{ width: `${(w.panther / maxWeek) * 100}%` }} title={`Panther : ${w.panther}`} />
                      )}
                    </div>
                    <span className="font-display text-lg text-white w-10 text-right shrink-0">{w.total}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {rows.length === 0 ? (
            <div data-testid="admin-empty" className="border border-dashed border-white/15 p-14 text-center text-white/50 flex flex-col items-center gap-4">
              <Inbox className="h-8 w-8 text-white/30" />
              Aucune demande pour le moment. Elles apparaîtront ici dès qu'un client enverra le formulaire.
            </div>
          ) : (
            <>
              <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
                <label className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <input
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setPage(1);
                    }}
                    data-testid="admin-search-input"
                    placeholder="Filtrer par référence, société ou marque..."
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-none h-12 pl-11 pr-4 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-blaze focus:ring-1 focus:ring-blaze"
                  />
                </label>
                <div className="flex gap-2" data-testid="admin-status-tabs">
                  {STATUS_TABS.map((t) => (
                    <button
                      key={t.key}
                      data-testid={`admin-status-${t.key}`}
                      onClick={() => {
                        setStatusFilter(t.key);
                        setPage(1);
                      }}
                      className={`text-sm px-5 py-2.5 border transition-colors duration-200 ${
                        statusFilter === t.key
                          ? "bg-blaze border-blaze text-white"
                          : "border-white/15 text-white/60 hover:text-white hover:border-white/40"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {visible.length === 0 ? (
                <div data-testid="admin-no-result" className="border border-dashed border-white/15 p-12 text-center text-white/50">
                  Aucune demande ne correspond à ce filtre.
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto border border-white/10" data-testid="admin-requests-table">
                    <table className="w-full text-sm min-w-[1100px]">
                      <thead>
                        <tr className="bg-black/50 border-b border-white/10 text-[11px] text-white/50 text-left">
                          {COLUMNS.map((c, i) => (
                            <th key={i} className="px-4 py-4 whitespace-nowrap font-medium">
                              {c.sortable ? (
                                <button
                                  onClick={() => sortBy(c.key)}
                                  data-testid={`admin-sort-${c.key}`}
                                  className={`flex items-center gap-1 hover:text-white transition-colors ${sortKey === c.key ? "text-blaze" : ""}`}
                                >
                                  {c.label}
                                  {sortKey === c.key &&
                                    (sortDir === "asc" ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />)}
                                </button>
                              ) : (
                                c.label
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.map((r) => (
                          <tr key={r.id} data-testid="admin-request-row" className={`border-b border-white/5 hover:bg-white/[0.03] transition-colors ${isProcessed(r) ? "opacity-60" : ""}`}>
                            <td className="px-4 py-4 text-white/60 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                            <td className="px-4 py-4 font-semibold whitespace-nowrap">{r.company}</td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className={`text-[11px] px-2 py-1 border ${r.brand === "PANTHER" ? "border-[#C32A3A] text-[#E8607A]" : "border-blaze/50 text-blaze"}`}>
                                {r.brand || "TREMBLAY"}
                              </span>
                            </td>
                            <td className="px-4 py-4 font-mono text-xs text-white/70 whitespace-nowrap">{r.siret}</td>
                            <td className="px-4 py-4 font-mono text-xs text-blaze whitespace-nowrap">
                            {r.reference}
                            {(r.selection || []).length > 0 && (
                              <span
                                data-testid={`admin-selection-${r.id}`}
                                className="ml-2 text-[10px] font-accent uppercase tracking-wider text-white/70 border border-white/20 px-1.5 py-0.5"
                                title={(r.selection || []).join("\n")}
                              >
                                +{r.selection.length}
                              </span>
                            )}
                          </td>
                            <td className="px-4 py-4 text-white/70">{r.quantity || "—"}</td>
                            <td className="px-4 py-4 whitespace-nowrap">{[r.first_name, r.last_name].filter(Boolean).join(" ")}</td>
                            <td className="px-4 py-4 text-white/70 whitespace-nowrap">{r.email}</td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className={`text-[11px] px-2 py-1 border ${isProcessed(r) ? "border-emerald-500/40 text-emerald-400" : "border-white/20 text-white/70"}`}>
                                {isProcessed(r) ? "Traité" : "Nouvelle"}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-white/50 max-w-[220px]"><span className="line-clamp-2">{r.message || "—"}</span></td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-2">
                                <a
                                  href={replyHref(r)}
                                  data-testid={`admin-reply-${r.id}`}
                                  className="flex items-center gap-2 text-[11px] border border-white/15 px-3 py-2 text-white/70 hover:text-blaze hover:border-blaze transition-colors w-fit whitespace-nowrap"
                                >
                                  <Mail className="h-3.5 w-3.5" /> Répondre
                                </a>
                                <button
                                  onClick={() => toggleProcessed(r)}
                                  disabled={rowBusy === r.id}
                                  data-testid={`admin-toggle-${r.id}`}
                                  className={`flex items-center gap-2 text-[11px] border px-3 py-2 transition-colors whitespace-nowrap disabled:opacity-50 ${
                                    isProcessed(r)
                                      ? "border-white/15 text-white/60 hover:text-white hover:border-white/40"
                                      : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                                  }`}
                                >
                                  {isProcessed(r) ? <RotateCcw className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
                                  {isProcessed(r) ? "Rouvrir" : rowBusy === r.id ? "..." : "Traiter"}
                                </button>
                                <button
                                  onClick={() => deleteRow(r)}
                                  disabled={rowBusy === r.id}
                                  data-testid={`admin-delete-${r.id}`}
                                  className="flex items-center gap-2 text-[11px] border border-white/15 px-3 py-2 text-white/50 hover:text-red-400 hover:border-red-400/50 transition-colors whitespace-nowrap disabled:opacity-50"
                                  title="Supprimer définitivement"
                                >
                                  <Trash2 className="h-3.5 w-3.5" /> Supprimer
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-5 mt-6" data-testid="admin-pagination">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={safePage === 1}
                        data-testid="admin-pagination-prev"
                        className="p-2 border border-white/15 text-white/70 hover:text-blaze hover:border-blaze disabled:opacity-25 disabled:hover:text-white/70 transition-colors"
                        aria-label="Page précédente"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="text-sm text-white/60 font-mono" data-testid="admin-pagination-indicator">
                        Page {safePage} / {totalPages} — {sorted.length} demande{sorted.length > 1 ? "s" : ""}
                      </span>
                      <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={safePage === totalPages}
                        data-testid="admin-pagination-next"
                        className="p-2 border border-white/15 text-white/70 hover:text-blaze hover:border-blaze disabled:opacity-25 disabled:hover:text-white/70 transition-colors"
                        aria-label="Page suivante"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </main>
      )}
    </div>
  );
}
